import { determineRootNodeId } from '../../../utils/dialogueUtils';
// Convert dialogue to tree structure
export const dialogueToTree = (dialogue) => {
    if (!dialogue.nodes || dialogue.nodes.length === 0) {
        return [];
    }
    const treeNodes = [];
    const nodeToParentMap = new Map(); // Map dialogueNode.id to its parent treeNodeId (which is a responseTreeId)
    dialogue.nodes.forEach(dialogueNode => {
        const dialogueTreeId = `dialogue_${dialogueNode.id}`;
        treeNodes.push({
            id: dialogueTreeId,
            parent: 0, // Root by default, will be changed if it is a continuation of a response
            text: dialogueNode.text || 'NPC Line',
            droppable: true, // NPC nodes can accept other NPC nodes (for reordering)
            data: {
                type: 'dialogue',
                nodeData: dialogueNode,
            },
        });
        if (dialogueNode.responseOptions) {
            dialogueNode.responseOptions.forEach((response) => {
                const responseTreeId = `response_${dialogueNode.id}_${response.id}`;
                treeNodes.push({
                    id: responseTreeId,
                    parent: dialogueTreeId, // A response is always a child of an NPC node
                    text: response.text || 'Player Response',
                    droppable: true, // Allow dropping onto responses for reorganization
                    data: {
                        type: 'response',
                        nodeData: Object.assign(Object.assign({}, response), { parentNpcNodeId: dialogueNode.id, 
                            // Preserve linkedToDialogueId if present
                            linkedToDialogueId: response.linkedToDialogueId }),
                    },
                });
                if (response.nextDialogueId && !response.linkedToDialogueId) {
                    // Create a parent-child link only when there is no linkedToDialogueId
                    // linkedToDialogueId takes priority over nextDialogueId
                    nodeToParentMap.set(response.nextDialogueId, responseTreeId);
                }
            });
        }
    });
    // Helper function to check whether assigning a parent would create a cycle
    const wouldCreateCycle = (childId, potentialParentId) => {
        if (childId === potentialParentId)
            return true;
        let currentParent = potentialParentId;
        while (currentParent !== 0) {
            const parentNode = treeNodes.find(n => n.id === currentParent);
            if (!parentNode)
                break;
            if (parentNode.id === childId)
                return true;
            currentParent = parentNode.parent;
        }
        return false;
    };
    // Set the correct parents for NPC nodes that are continuations of responses
    treeNodes.forEach(tn => {
        var _a;
        if (((_a = tn.data) === null || _a === void 0 ? void 0 : _a.type) === 'dialogue') {
            const dialogueNodeId = tn.data.nodeData.id;
            if (nodeToParentMap.has(dialogueNodeId)) {
                const potentialParent = nodeToParentMap.get(dialogueNodeId);
                // Skip the assignment if it creates a cycle
                if (!wouldCreateCycle(tn.id, potentialParent)) {
                    tn.parent = potentialParent;
                }
                else {
                    // If it is a cycle, keep it at the root
                    tn.parent = 0;
                }
            }
            else if (dialogueNodeId !== dialogue.rootNodeId) {
                // If the node is not the root and is not a continuation of a response, it must be at the root (for disconnected branches)
                tn.parent = 0; // or a specific ID for "orphans" if needed
            }
        }
    });
    // Ensure rootNodeId always has parent 0 if it is not a continuation of a response
    const rootNodeInTree = treeNodes.find(tn => { var _a; return ((_a = tn.data) === null || _a === void 0 ? void 0 : _a.type) === 'dialogue' && tn.data.nodeData.id === dialogue.rootNodeId; });
    if (rootNodeInTree && !nodeToParentMap.has(dialogue.rootNodeId)) {
        rootNodeInTree.parent = 0;
    }
    return treeNodes;
};
// Convert tree back to dialogue
export const treeToDialogue = (treeData, dialogue) => {
    const newNodes = [];
    const nodeMap = new Map(); // Map for fast access to the NPC nodes being created
    // First create all NPC nodes from the tree IN THE ORDER THEY APPEAR IN THE TREE
    // Sort by position in the tree instead of just iterating
    const sortedDialogueNodes = treeData
        .filter(treeNode => { var _a; return ((_a = treeNode.data) === null || _a === void 0 ? void 0 : _a.type) === 'dialogue'; })
        .sort((a, b) => {
        // Sort by position in the treeData array
        return treeData.indexOf(a) - treeData.indexOf(b);
    });
    sortedDialogueNodes.forEach(treeNode => {
        var _a;
        if (((_a = treeNode.data) === null || _a === void 0 ? void 0 : _a.type) === 'dialogue') {
            // Clone so we do not mutate the original nodeData from the useImmer state directly
            const clonedNodeData = JSON.parse(JSON.stringify(treeNode.data.nodeData));
            clonedNodeData.responseOptions = []; // Clear responseOptions, they will be filled below
            newNodes.push(clonedNodeData);
            nodeMap.set(clonedNodeData.id, clonedNodeData);
        }
    });
    // Then process responses and links
    treeData.forEach(treeNode => {
        var _a, _b;
        if (((_a = treeNode.data) === null || _a === void 0 ? void 0 : _a.type) === 'response') {
            const responseData = treeNode.data.nodeData;
            const parentNpcNode = nodeMap.get(responseData.parentNpcNodeId);
            if (parentNpcNode) {
                const nextDialogueChildNode = treeData.find(n => { var _a; return n.parent === treeNode.id && ((_a = n.data) === null || _a === void 0 ? void 0 : _a.type) === 'dialogue'; });
                let nextDialogueId = undefined;
                // Determine the link type for the response.
                // 1) If the response has a child NPC node in the tree, it is a hierarchical (nested) link,
                //    in which case we store it in nextDialogueId.
                // 2) If there is no child node but linkedToDialogueId is set, it is a cross (link)
                //    link, and in that case we leave the nextDialogueId FIELD undefined so that
                //    link types are distinguished correctly in ScenarioEditor.
                if (((_b = nextDialogueChildNode === null || nextDialogueChildNode === void 0 ? void 0 : nextDialogueChildNode.data) === null || _b === void 0 ? void 0 : _b.type) === 'dialogue') {
                    // nested link
                    nextDialogueId = nextDialogueChildNode.data.nodeData.id;
                }
                const responseWithLink = responseData;
                const newResponse = Object.assign(Object.assign({ id: responseData.id, text: responseData.text, requiredCharacteristic: responseData.requiredCharacteristic, requiredCharacteristicValue: responseData.requiredCharacteristicValue, rewardLevel: responseData.rewardLevel }, (nextDialogueId ? { nextDialogueId } : {})), (responseWithLink.linkedToDialogueId ? { linkedToDialogueId: responseWithLink.linkedToDialogueId } : {}));
                parentNpcNode.responseOptions = parentNpcNode.responseOptions || [];
                parentNpcNode.responseOptions.push(newResponse);
            }
        }
    });
    // Use the shared root-node determination function
    const newRootNodeId = determineRootNodeId(Object.assign(Object.assign({}, dialogue), { nodes: newNodes }));
    return Object.assign(Object.assign({}, dialogue), { nodes: newNodes, rootNodeId: newRootNodeId });
};
